import '../base/buttons/as-icon-button';
import './subcomps/as-home-button';
import './subcomps/as-session-set.ts'
import { TrSessionSet } from './subcomps/as-session-set';
import './subcomps/as-view-set';
import './subcomps/as-state-set';
import '../aesthetic/fade-container';
import '../overlays/as-popout-button';
import '../text-and-lang/as-text';
import '../base/as-spinner';
import '../overlays/as-tooltip-wrapper.ts';
import { hasEnabledAttribute } from '../wcUtil.ts';
import { liveAppData, IconList } from '../../store/store';
import { logOut, getUserData } from '../../logic/asApi';
import { getLicenseInfo } from '../../logic/license.ts';
import { devlog } from '../../logic/utils';
import { fullAppSuiteRootURL, suppressExternalServices, currentEnv } from '../../data/constants.ts';

import styleText from './as-header.css?raw';

// Source - https://stackoverflow.com/a
// Posted by Harshal Patil
// Retrieved 2025-11-10, License - CC BY-SA 4.0

// Read SCSS file as a raw CSS text using Webpack/Rollup/Parcel

const sheet = new CSSStyleSheet();
sheet.replaceSync(styleText);

/**
 * Main header component that composes all section components
 */
export class TrHeader extends HTMLElement {
  // private readonly headerImgDir = new URL('assets/img/header/', import.meta.url).pathname ;
  private readonly headerImgDir = 'assets/img/header/'
  private cleanedTitle = 'App';
  private isSigningOut = false;
  private popoutObserver: MutationObserver | null = null;
  private envObserver: MutationObserver | null = null;
  
  static get observedAttributes(): string[] {
    return ['app-title','no-session-icons','no-view-icons','no-state-icons','no-default-icons','show-lang-selector'];
  }

  get projectTitle(): string {
    return (this.shadowRoot?.querySelector('as-session-set') as TrSessionSet | null)?.projectTitle ?? '';
  }

  set projectTitle(value: string) {
    const sessionSet = this.shadowRoot?.querySelector('as-session-set') as TrSessionSet | null;
    if (sessionSet) sessionSet.projectTitle = value;
  }

  private handleAuthUpdate = (event: CustomEvent) => {
    devlog('Auth update received:', event.detail);
    this.updateUserOptionsPanel();
  };

  private handleSignOut = async (event: Event) => {
    event.preventDefault();
    this.isSigningOut = true;
    this.updateSignOutButton();
    try {
      await logOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      this.isSigningOut = false;
      this.updateSignOutButton();
    }
  };

  private updateSignOutButton = () => {
    // Find the sign out link in the popout content
    const popoutButton = this.shadowRoot?.querySelector('#trincklePopout');
    if (popoutButton) {
      const signOutLink = popoutButton.querySelector('#signOutLink');
      if (signOutLink) {
        signOutLink.innerHTML = this.isSigningOut 
          ? '<as-spinner style="width: 1rem; height: 1rem;"></as-spinner>' 
          : '<as-text dict-key="signOutLink">Sign Out</as-text>';
      }
    }
  };

  private handleManageAccount = (event: Event) => {
    event.preventDefault();
    // Open the manage account modal in a new tab
    window.open(`${fullAppSuiteRootURL}/?modal=manage-user-view`, '_blank');
  };

  private generateUserOptionsPanel(): string {
    if (!liveAppData.userData) {
      // Show placeholders when data is loading
      return /*html*/`
        <!-- <link rel="stylesheet" href="./as-header.css"> -->
        <div id="userOptionsPanel" style="display: flex; flex-direction: column; gap: 1rem; min-width: 200px;">
          <span id="userGreeting" style="padding-bottom: 1rem;">
            Hello <span class="placeholder name"></span>
          </span>

          <span id="userEmail">
            <span class="placeholder email"></span>
          </span>

          <div style="display: flex; flex-direction: row;">
            <a id="manage-account-button" href="#" data-action="manage-account">
              <as-text dict-key="manageAccountLink">Manage Account</as-text>
            </a>
          </div>

          <a id="signOutLink" href="#" data-action="sign-out">
            <as-text dict-key="signOutLink">Sign Out</as-text>
          </a>
        </div>
      `;
    }

    const userData = liveAppData.userData;
    const userName = userData.firstName && userData.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : 'User';
    
    const organization = userData.organization ? `(${userData.organization})` : '';
    const email = userData.eMail || '';

    const licenseInfo = getLicenseInfo(userData.license);
    const licenseHTML = licenseInfo.hasLicense
      ? /*html*/`<span id="licenseInfo"><as-text dict-key="license.label">License:</as-text> ${licenseInfo.label}</span>`
      : /*html*/`<span id="licenseInfo" class="license-warning"><as-text dict-key="license.label">License:</as-text> <as-text dict-key="license.none">No license</as-text></span>`;

    return /*html*/`
      <div id="userOptionsPanel" style="display: flex; flex-direction: column; gap: 1rem; min-width: 200px;">
        <span id="userGreeting" style="padding-bottom: 1rem;">
          Hello ${userName} ${organization}
        </span>

        ${email ? /*html*/`<span id="userEmail">${email}</span>` : ''}

        ${licenseHTML}

        <div style="display: flex; flex-direction: row;">
          <a id="manage-account-button" href="#" data-action="manage-account">
            <as-text dict-key="manageAccountLink">Manage Account</as-text>
          </a>
        </div>

        <a id="signOutLink" href="#" data-action="sign-out">
          <as-text dict-key="signOutLink">Sign Out</as-text>
        </a>
      </div>
    `;
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;

    if (name === 'app-title') {
      const hasText = newValue && newValue.replaceAll(' ', '').length > 0;
      this.cleanedTitle = hasText ? newValue : 'App';
      // Surgical update of the title if it already exists to avoid layout flicker
      const titleEl = this.shadowRoot?.querySelector('#header-title');
      if (titleEl) {
        titleEl.textContent = this.cleanedTitle;
        // Also update dict-key if it was intended to follow app-title
        // But for now just content is enough if dict-key is not used
        return;
      }
    }
    this.render();
  }

  render(){
    const liveEnv = document.querySelector('as-app')?.getAttribute('env') ?? null;

    this.shadowRoot!.innerHTML = /*html*/`
    <div class="header-content">
      <div class="header-left">
        <as-home-button></as-home-button>
        <as-text id="header-title" dict-key="${this.getAttribute('app-title') || ''}">${  this.cleanedTitle }</as-text>
      </div>

      <slot name="slot-0"></slot>
      ${hasEnabledAttribute(this, 'no-session-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-session-set class="icon-group"></as-session-set>`}
      <slot name="slot-1"></slot>
      ${hasEnabledAttribute(this, 'no-view-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-view-set class="icon-group"></as-view-set>`}
      <slot name="slot-2"></slot>
      ${hasEnabledAttribute(this, 'no-state-icons') || hasEnabledAttribute(this, 'no-default-icons') ? '' : `<as-state-set class="icon-group"></as-state-set>`}
      <slot name="slot-3"></slot>

      <div class="header-right">
        <!-- Clickthrough help link is projected here from light DOM (see
             _updateHelpLink) so its <a target="_blank"> is reachable by the
             GrabCAD host's link interception — a shadow-DOM anchor is retargeted
             to the host element and never opens. -->
        <slot name="help-link"></slot>

        ${!suppressExternalServices && liveEnv !== 'grabcad' ? `
        <as-popout-button
          button-type="icon"
          icon-name="user-options"
          file-name="user.png"
          id="trincklePopout"
          popout-placement="bottom-left"
          tooltip-dict-key="tooltip.userOptions"
          tooltip-default-text="User options"
          tooltip-placement="bottom-left"
        >
          ${this.generateUserOptionsPanel()}
        </as-popout-button>
        ` : `
        <as-icon-button
          icon-name="user-options"
          file-name="user.png"
          style="pointer-events: none;"
        ></as-icon-button>
        `}
        ${!hasEnabledAttribute(this, 'show-lang-selector') ? '' : `<as-lang-selector dict-key="langSelector"></as-lang-selector>`}
      </div>
    </div>
  `;
    this._updateHelpLink();
  }

  /**
   * Keep the clickthrough help link in sync as a *light-DOM* child of this
   * header, projected into the shadow `help-link` slot.
   *
   * It must be a real, connected, plain `<a target="_blank">` with no nested
   * interactive element, for the GrabCAD host:
   *   - scripted opens are blocked there (window.open / a detached anchor's
   *     .click() — what as-text-button's `link` does — do nothing), so the
   *     anchor has to be a genuine in-DOM element the user actually clicks;
   *   - a nested `<button>` (e.g. as-icon-button) has its own activation
   *     behaviour and would swallow the anchor's, so we use a bare `<img>`;
   *   - it must be light DOM — a click inside a shadow root is retargeted to the
   *     shadow host, so a shadow-DOM anchor never resolves as a link for the
   *     host. The home page's links work for exactly these reasons.
   *
   * The localized/flash tooltip lives in an as-tooltip-wrapper placed *inside*
   * the anchor, around the <img> only — the wrapper just slots the img, so the
   * anchor stays the clean light-DOM activatable element (no nested button, no
   * shadow boundary around the anchor itself).
   */
  private _updateHelpLink(): void {
    const clickthruUrl = this.closest('as-app')?.getAttribute('clickthru-url') ?? '';
    const show = !suppressExternalServices && !!clickthruUrl;

    let link = this.querySelector<HTMLAnchorElement>(':scope > a.header-help-link');
    if (!show) { link?.remove(); return; }

    if (!link) {
      link = document.createElement('a');
      link.className = 'header-help-link';
      link.slot = 'help-link';
      link.target = '_blank';
      // Slotted-descendant img isn't reachable by the header's ::slotted rules,
      // so size/colour it inline (brightness(0) = the old `black` icon look).
      link.innerHTML = /*html*/`<as-tooltip-wrapper
        show-on-hover
        placement="bottom-left"
        dict-key="header.help.tooltip"
        default-text="Need help? Click here for a tutorial."
        flash
        flash-duration="3000"
      ><img src="${IconList.help.default}" alt="Help" style="height:1.8rem; width:auto; filter:brightness(0);" /></as-tooltip-wrapper>`;
      this.appendChild(link);
    }
    link.href = clickthruUrl;
  }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Listen for auth updates
    window.addEventListener('as-auth-update', this.handleAuthUpdate);
    
    this.render();
  }

  connectedCallback() {
    // Check for empty slots after the component is connected to the DOM
    this.setupSlotListeners();

    // Use MutationObserver to watch for popout content and attach event listeners
    this.setupPopoutObserver();
    // Use the sheet inside the web component constructor
    this.shadowRoot.adoptedStyleSheets = [sheet];

    const app = document.querySelector('as-app');
    if (app) {
      this.envObserver = new MutationObserver(() => this.render());
      this.envObserver.observe(app, { attributes: true, attributeFilter: ['env', 'clickthru-url'] });
    }

    // The constructor's render() ran before we were in the DOM, so closest(as-app)
    // was null and no help link could be built yet. Sync it now we're connected.
    this._updateHelpLink();
  }

  private setupPopoutObserver() {
    this.popoutObserver = new MutationObserver(() => {
      this.attachPopoutEventListeners();
    });

    // Start observing the shadow root for changes
    this.popoutObserver.observe(this.shadowRoot!, {
      childList: true,
      subtree: true
    });

    // Also try to attach listeners immediately in case content is already there
    setTimeout(() => this.attachPopoutEventListeners(), 100);
  }

  private attachPopoutEventListeners() {
    const popoutButton = this.shadowRoot?.querySelector('#trincklePopout');
    if (popoutButton) {
      // Add click handler to update panel content when popout is opened
      if (!popoutButton.hasAttribute('data-click-listener-attached')) {
        popoutButton.setAttribute('data-click-listener-attached', 'true');
        popoutButton.addEventListener('click', () => {
          this.updateUserOptionsPanel();
        });
      }

      const signOutLink = popoutButton.querySelector('#signOutLink');
      const manageAccountButton = popoutButton.querySelector('#manage-account-button');

      if (signOutLink && !signOutLink.hasAttribute('data-listener-attached')) {
        signOutLink.setAttribute('data-listener-attached', 'true');
        signOutLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          devlog('Sign out clicked');
          this.handleSignOut(e);
        });
      }

      if (manageAccountButton && !manageAccountButton.hasAttribute('data-listener-attached')) {
        manageAccountButton.setAttribute('data-listener-attached', 'true');
        manageAccountButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          devlog('Manage account clicked');
          this.handleManageAccount(e);
        });
      }
    }
  }

  private updateUserOptionsPanel() {
    const popoutButton = this.shadowRoot?.querySelector('#trincklePopout');
    if (popoutButton) {
      // Update the innerHTML of the popout button with fresh user data
      popoutButton.innerHTML = this.generateUserOptionsPanel();
      
      // Re-attach event listeners since we just replaced the content
      setTimeout(() => this.attachPopoutEventListeners(), 50);
    }
  }

  /**
   * Sets up listeners for slot content changes to add/remove hidden class
   */
  private setupSlotListeners(): void {
    const slots = this.shadowRoot!.querySelectorAll('slot');
    slots.forEach(slot => {
      // Initial check
      this.checkSlotContent(slot);
      
      // Listen for changes
      slot.addEventListener('slotchange', () => {
        this.checkSlotContent(slot);
      });
    });
  }
  
  /**
   * Checks if a slot has assigned nodes and hides empty slots
   */
  private checkSlotContent(slot: HTMLSlotElement): void {
    // Check for any non-whitespace text or element nodes
    const hasContent = Array.from(slot.assignedNodes()).some(node => {
      if (node.nodeType === Node.ELEMENT_NODE) return true;
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true;
      return false;
    });
    
    // Toggle the empty-slot class based on content
    if (hasContent) {
      slot.classList.remove('empty-slot');
    } else {
      slot.classList.add('empty-slot');
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    window.removeEventListener('as-auth-update', this.handleAuthUpdate);

    if (this.popoutObserver) {
      this.popoutObserver.disconnect();
      this.popoutObserver = null;
    }
    if (this.envObserver) {
      this.envObserver.disconnect();
      this.envObserver = null;
    }
  }
}

customElements.define('as-header', TrHeader); 